/* global common, footer, AskAQuestion, AvailabilitySection */

import {
  sendGoogleEvent,
  generalQuestionId,
  loadJs,
  checkString,
  scriptUrl,
  sendRequest,
} from '@html/_common/utils';
import {
  travellerEnquiryStore,
} from '@html/_common/footer_script';

import AMCommonMonthSelector from '@atomic/molecules/_common/month-selector/month-selector';
import AMCommonQuantity from '@atomic/molecules/_common/quantity/quantity';
import {
  getAvailabilitySectionClass,
  getAvailabilityDeparturesSectionClass,
} from '@atomic/organisms/tour_b/availability/consts';

export default function AOTourPrivateRequestInteractions(tourId, operatorId) {
  this.common = undefined;
  this.footer = {};

  if (typeof sendGoogleEvent !== 'undefined') {
    this.common = {
      func: { checkString },
      sendGoogleEvent,
      generalQuestionId,
      loadJs,
      scriptUrl,
      sendRequest,
    };
  } else {
    this.common = common;
  }

  if (typeof travellerEnquiryStore !== 'undefined') {
    this.footer = { travellerEnquiryStore };
  } else {
    this.footer = footer;
  }

  this.tourId = tourId || this.common.generalQuestionId;
  this.operatorId = operatorId;
  this.isPrivateOfTheShelf = document.querySelector('main').dataset.private === '1';

  this.requestCompleted = false;
  this.isFormInteractionStarted = false;

  this.TEMPLATES_ENDPOINT = '/api/popup/ask-question';
  this.INPUT_CLASS = 'am-input';
  this.LARGE_GROUP = 4; // min pax number to be considered as a large group

  // GA
  this.eventCategory = 'Tour Detail';
  this.eventAction = 'Private Tours - Find out more';
  this.snapshotEventAction = 'Private Tours - Tour Snapshot';
  this.personalizedPanelEventAction = 'Private Tours - Personalized Panel';
  this.eventActionSuccess = 'Private Tours - Request Quote - BCP';
  this.eventActionCanceled = 'Private Tours - Request Quote - Information Missing';
  this.personalizedFormInteraction = 'Personalized Form Interaction';

  this.availabilityAction = 'Tour Type Toggle';
  this.hiddenClass = 'hid';

  // TDP availability switch
  this.availabilityId = 'availability-options';
  this.isPopupMode = false;
  this.selectedAvailability = this.isPrivateOfTheShelf ? 'private' : 'group';
  this.previousAvailability = 'group';
  this.activeCs = 'ao-tour-availability__type-switch--active';
  this.loadingCs = 'ao-tour-availability__type-switch--loading';

  this.availabilityGroupHtml = null;
  this.availabilityPersonalizedHtml = null;

  this.ALTERNATIVE_DROPDOWN_SELECTOR = '.js-ao-tour-availability__select--alternative .js-aa-dropdown';

  this.numberOfChildrenTravellers = 0;

  this.containerId = () => (this.isPopupMode ? 'private-request_popup' : this.availabilityId);

  this.onErrorRequest = ($content, $submitButton) => {
    const requestFailedText = $content.data('request-failed');
    const submitButtonLabelText = $content.data('submit-button-label');

    alert(requestFailedText);

    $submitButton.html(submitButtonLabelText).removeClass('wait');
  };

  /**
   * Scrolls to the specified element.
   * @param {HTMLElement} elem - The element to scroll to.
   */
  this.scrollByElemData = (elem) => {
    let scrollToElement;
    if (typeof getAvailabilitySectionClass !== 'undefined') {
      scrollToElement = elem.dataset.scrollTo === 'check'
        ? getAvailabilitySectionClass()
        : getAvailabilityDeparturesSectionClass();
    } else {
      scrollToElement = elem.dataset.scrollTo === 'check'
        ? AvailabilitySection.getAvailabilitySectionClass()
        : AvailabilitySection.getAvailabilityDeparturesSectionClass();
    }
    const content = document.querySelector(`.${scrollToElement}`);
    if (content) {
      content.scrollIntoView({ behavior: 'smooth' });
    }
  };

  this.handleAboveFoldTrigger = () => {
    this.common.sendGoogleEvent(
      this.eventCategory,
      this.snapshotEventAction,
      this.tourId,
      {
        ampEventName: 'request_private_form_opened',
      },
    );
    this.loadForm();
  };

  this.handleAdventureTypeTrigger = () => {
    this.common.sendGoogleEvent(
      this.eventCategory,
      this.personalizedPanelEventAction,
      `${this.tourId} - ${this.isPrivateOfTheShelf ? 'Private TDP' : 'Group TDP'}`,
    );
    this.loadForm();
  };

  this.handlePersonalizedTabClick = (e) => {
    e.preventDefault();
    const $el = $(e.target);

    if (this.isAvailabilitySelectedOrLoading('personalized')) return;
    this.selectedAvailability = 'personalized';

    $el.addClass(`${this.activeCs} ${this.loadingCs}`)
      .siblings('button').removeClass(this.activeCs);

    this.loadForm(false);

    this.common.sendGoogleEvent(
      this.eventCategory,
      this.availabilityAction,
      'Personalized Tour',
    );
  };

  this.handleGroupTabClick = (e) => {
    e.preventDefault();
    const $el = $(e.target);
    // eslint-disable-next-line no-shadow
    const tourId = $el.data('tourid');
    this.previousAvailability = this.selectedAvailability;

    if (this.isAvailabilitySelectedOrLoading('group')) {
      this.scrollByElemData(e.target);
      return;
    }
    this.selectedAvailability = 'group';

    $el.addClass(`${this.activeCs} ${this.loadingCs}`)
      .siblings('button').removeClass(this.activeCs);

    this.renderAvailabilityBlock();
    $(this.ALTERNATIVE_DROPDOWN_SELECTOR).val(tourId).trigger('change');

    this.common.sendGoogleEvent(
      this.eventCategory,
      this.availabilityAction,
      'Group Tour',
    );
    this.scrollByElemData(e.target);
  };

  this.handlePrivateTabClick = (e) => {
    e.preventDefault();
    const $el = $(e.target);
    // eslint-disable-next-line no-shadow
    const tourId = $el.data('tourid');
    this.previousAvailability = this.selectedAvailability;

    if (this.isAvailabilitySelectedOrLoading('private')) {
      this.scrollByElemData(e.target);
      return;
    }
    this.selectedAvailability = 'private';

    $el.addClass(`${this.activeCs} ${this.loadingCs}`)
      .siblings('button').removeClass(this.activeCs);

    this.renderAvailabilityBlock();
    $(this.ALTERNATIVE_DROPDOWN_SELECTOR).val(tourId).trigger('change');

    this.common.sendGoogleEvent(
      this.eventCategory,
      this.availabilityAction,
      'Private Tour',
    );
    this.scrollByElemData(e.target);
  };

  this.loadForm = (isPopupMode = true) => {
    this.isPopupMode = isPopupMode;

    if (!$('.js-private-request__content').length && !this.availabilityPersonalizedHtml) {
      return this.common.sendRequest(this.TEMPLATES_ENDPOINT, {
        action: 'private',
        tourId: this.tourId,
        operatorId: this.operatorId,
      }, (data) => {
        if (data.success === true) {
          $('main').append(data.component);
          if (typeof sendGoogleEvent !== 'undefined') {
            import(/* webpackChunkName: "AskAQuestion" */'@html/_common/question/script').then((module) => {
              const AskAQuestion = module.default;
              this.initializeForm(AskAQuestion);
            });
          } else if (window.AskAQuestion) {
            this.initializeForm();
          } else {
            this.common.loadJs(this.common.scriptUrl.askAQuestion, this.initializeForm);
          }
          this.showForm();
        }
      });
    }

    return this.showForm();
  };

  this.handlePaxChange = (event) => {
    const $container = $(`#${this.containerId()}`);
    const $phoneLabel = $container
      .find('label[for="private-request-phone"]');
    const $phoneField = $container.find('#private-request-phone');

    const additionalPax = parseInt(
      $container.find(`input[name=${event.target.name === 'pax' ? 'children' : 'pax'}]`).val(),
      10,
    );
    const totalPax = parseInt(event.target.value, 10) + additionalPax;

    $phoneLabel.html(
      $phoneField.data(
        totalPax < this.LARGE_GROUP ? 'optional' : 'required',
      ),
    );
  };

  // eslint-disable-next-line consistent-return
  this.handleFormSubmit = (event) => {
    event.preventDefault();

    const $thePopup = $(`#${this.containerId()}`);
    const $submitButton = $thePopup.find('button[type=submit]');

    if ($submitButton.hasClass('wait')) {
      return false;
    }

    // fill in the tour data
    const $form = $(event.target);
    const currency = $('main')
      .data('curr');

    $form.find('[name=tour_id]')
      .val(this.tourId);
    $form.find('[name=currency]')
      .val(currency);

    // validate
    const fieldsToValidate = [
      { name: 'first_name' },
      { name: 'phone' },
      { name: 'approximate_date' },
      { name: 'email', validateFn: (val) => !!val && this.common.func.checkString(val, 'email') },
    ];

    const $minPax = parseInt($form.find('input[name=min_pax]')
      .val(), 10);
    if ($minPax) {
      fieldsToValidate.push(...[
        {
          name: 'pax',
          validateFn: (val) => {
            const $children = $form.find('input[name=children]');
            const totalPax = parseInt(val, 10) + parseInt($children.val(), 10);
            return totalPax >= $minPax;
          },
        },
        {
          name: 'children',
          validateFn: (val) => {
            const $pax = $form.find('input[name=pax]');
            const totalPax = parseInt(val, 10) + parseInt($pax.val(), 10);
            return totalPax >= $minPax;
          },
        },
      ]);
    }

    const isFormValid = fieldsToValidate
      .reduce((acc, field) => {
        const isValid = this.isFieldValid(
          $form.find(`input[name=${field.name}]`),
          field.validateFn,
        );
        return acc && isValid;
      }, true);

    if (isFormValid) {
      this.sendEnquiryRequest();
    }
    return isFormValid;
  };

  this.initializeForm = (AskAQuestionParam) => {
    const onPaxChange = this.handlePaxChange.bind(this);
    const onSubmit = this.handleFormSubmit.bind(this);

    const sendEvent = () => {
      if (this.isFormInteractionStarted) return;
      const eventLabel = `${this.isPopupMode ? 'Quote Request' : 'Embedded'} - ${this.isPrivateOfTheShelf ? 'Private TDP' : 'Group TDP'}`;

      this.common.sendGoogleEvent(
        this.eventCategory,
        this.personalizedFormInteraction,
        eventLabel,
      );

      this.isFormInteractionStarted = true;
    };

    $('.js-private-request__content')
      .on('change', 'input[name=pax], input[name=children]', onPaxChange)
      .on('submit', 'form', onSubmit)
      .on('click', 'form', sendEvent);

    new AMCommonQuantity().initiate('js-private-request__children');
    new AMCommonQuantity().initiate('js-private-request__adults');

    new AMCommonMonthSelector().initiate({
      $container: $('.js-private-ask-form-approximate_date'),
    });

    const question = AskAQuestionParam ? new AskAQuestionParam() : new AskAQuestion();
    question.initTextArea();

    if (this.tourId !== this.common.generalQuestionId) {
      question.listenEmail($('.js-private-request__content').find('form'), 'ask-private-request');
    }

    $('body').on('click', '.js-ao-tour-private-request__close-btn', () => {
      this.closePopup();
    });
  };

  this.sendAppScriptRequest = () => {
    const $content = $('.js-private-request__content');
    const $form = $content.find('form');
    const redirectUrl = $form.find('input[name=bcp_link]').val();

    const url = 'https://script.google.com/macros/s/AKfycbwL4dCPH_ji-fKvHgk1PckU_K72klAMrWzbSaygj34DL4fWQeqc/exec';
    const type = 'POST';

    // temporarily remove disabled for values to be processed
    const disabled = $form.find('input:disabled').removeAttr('disabled');

    const data = JSON.stringify($form
      .serializeArray()
      .reduce((acc, n) => {
        acc[n.name] = n.value;
        return acc;
      }, {}));

    disabled.attr('disabled', 'disabled');

    $.ajax({
      url,
      type,
      data,
      timeout: 0,
      dataType: 'json',
      success: (response) => {
        this.requestCompleted = true;

        this.common.sendGoogleEvent(
          this.eventCategory,
          response.result !== 'success' ? this.eventActionSuccess : 'Private Tours - Request Quote - App Script Request Failed',
          this.isPopupMode ? 'Modal' : 'Embedded',
        );

        this.checkNewsletterSignup($content);
        window.location.href = redirectUrl;

        return true;
      },
      error: () => {
        window.location.href = redirectUrl;
        return true;
      },
    });
  };

  this.sendEnquiryRequest = () => {
    const $content = $('.js-private-request__content');
    const $submitButton = $('.js-private-request__submit');
    const $form = $content.find('form');

    const waitingText = $content.data('waiting');
    $submitButton.html(waitingText).addClass('wait');

    const emailValue = $.trim($form.find('[name="email"][type="email"]').val());
    const firstNameValue = $form.find('input[name=first_name]').val();
    const lastNameValue = $form.find('input[name=last_name]').val();
    const phoneValue = $form.find('input[name=phone]').val();
    const numberOfAdultTravellers = parseInt($form.find('input[name=pax]').val(), 10) || 0;
    const numberOfChildrenTravellers = parseInt($form.find('input[name=children]').val(), 10) || 0;
    const request = $form.find('textarea[name=request]').val() || '';
    const approximateDate = $form.find('input[name=approximate_date]').val();
    const currency = $form.find('[name=currency]').val();
    const hasServiceFee = Boolean($form.find('[name=has_service_fee]').val());
    const serviceFee = $form.find('[name=service_fee]').val();
    const operatorName = $form.find('[name=operator_name]').val();
    const tokenValue = $form.find('[name=token_value]').val();

    const csrf = $form.find('#js-ao-settings-security-input').val() || '';
    const csrfInputName = $form.find('#js-ao-settings-security-input').attr('name') || 'tr_csrf_cookie_name';

    const data = {
      type: 't',
      id: this.tourId,
      email: emailValue,
      first_name: firstNameValue,
      last_name: lastNameValue,
      details_phone: phoneValue,
      source: 'tr',
      source_id: 0,
      pax: numberOfAdultTravellers,
      pax_children: numberOfChildrenTravellers,
      details_type: 4,
      request,
      note: '',
      approximate_date: approximateDate,
      currency,
      display_dates_popup: false,
      booktype: 'private',
      [csrfInputName]: csrf,
    };

    if ($form.find('[name="email"][type="email"]').data('email')) {
      data.as_ta = true;
    }

    this.numberOfChildrenTravellers = numberOfChildrenTravellers;

    this.common.sendRequest($form.attr('action'), data, (requestData) => {
      if (requestData.status !== 'OK') {
        this.onErrorRequest($content, $submitButton);

        return false;
      }

      $form.find('input[name=bcp_link]').val(requestData.href);

      this.footer.travellerEnquiryStore.handleEnquiryCreated(requestData.href);

      if (requestData.id) {
        this.common.sendGoogleEvent(
          'Enquiry Created',
          'Private Request',
          requestData.id,
          {
            tourId: requestData.id,
            operator_name: operatorName,
            token_value: tokenValue,
            service_fee: serviceFee,
            has_service_fee: hasServiceFee,
            ampEventName: 'request_private_form_completed',
            question_id: requestData.id,
          },
        );
      }

      this.sendAppScriptRequest(requestData.isOBS);

      return true;
    }, {}, () => {
      this.onErrorRequest($content, $submitButton);
    });

    return true;
  };

  this.showForm = () => {
    if (this.isPopupMode === true) {
      this.openPopup();
      return;
    }
    this.renderAvailabilityBlock();
  };

  this.isFieldValid = ($theField, validationFn = ((val) => !!val)) => {
    const $theWrapper = $theField.parents(`.${this.INPUT_CLASS}`);
    const errorClass = `${this.INPUT_CLASS}--error`;

    if (validationFn($.trim($theField.val()))) {
      return !!$theWrapper.removeClass(errorClass);
    }

    $theField
      .on('change paste keyup', () => {
        if (validationFn($theField.val())) {
          $theWrapper.removeClass(errorClass);
        }
      });
    return !$theWrapper.addClass(errorClass);
  };

  this.renderPopupForm = () => {
    const $contentWrap = $(`#${this.containerId()} .js-private-request__wrap`);
    if ($contentWrap.children().length) {
      return;
    }

    if (this.selectedAvailability !== 'personalized' && this.availabilityPersonalizedHtml) {
      this.availabilityPersonalizedHtml.appendTo($contentWrap);
      return;
    }

    this.availabilityPersonalizedHtml = $(`#${this.availabilityId}`).children().detach();
    this.availabilityGroupHtml.appendTo(`#${this.availabilityId}`);

    this.availabilityPersonalizedHtml.appendTo($contentWrap);
  };

  this.isAvailabilitySelectedOrLoading = (type) => this.selectedAvailability === type || $('.ao-tour-availability__type-switch').hasClass(this.loadingCs);

  this.renderAvailabilityBlock = () => {
    $('.ao-tour-availability__type-switch')
      .removeClass('ao-tour-availability__type-switch--loading');

    if (this.selectedAvailability === 'personalized') {
      this.availabilityGroupHtml = $(`#${this.containerId()}`).children().detach();
      if (!this.availabilityPersonalizedHtml) {
        this.availabilityPersonalizedHtml = $('.js-private-request__content').detach();
      }
      this.availabilityPersonalizedHtml.appendTo(`#${this.containerId()}`);
      return;
    }

    if (this.availabilityGroupHtml && this.previousAvailability === 'personalized') {
      this.availabilityPersonalizedHtml = $(`#${this.containerId()}`).children().detach();
      this.availabilityGroupHtml.appendTo(`#${this.containerId()}`);
    }
  };

  this.openPopup = () => {
    this.renderPopupForm();
    $('body').toggleClass('layer', true);
    $('html').toggleClass('layer', true);
    $('.js-ao-tour-private-request-popup__overlay').toggleClass(this.hiddenClass, false);
  };

  this.closePopup = () => {
    $('body').toggleClass('layer', false);
    $('html').toggleClass('layer', false);
    $('.js-ao-tour-private-request-popup__overlay').toggleClass(this.hiddenClass, true);

    this.isPopupMode = false;

    if (this.selectedAvailability === 'personalized') {
      this.availabilityGroupHtml = $(`#${this.containerId()}`).children().detach();
      if (!this.availabilityPersonalizedHtml) {
        this.availabilityPersonalizedHtml = this.$content.detach();
      }
      this.availabilityPersonalizedHtml.appendTo(`#${this.containerId()}`);
    }

    if (this.requestCompleted) {
      return false;
    }

    return this.common.sendGoogleEvent(
      this.eventCategory,
      this.eventActionCanceled,
      this.tourId,
    );
  };

  this.checkNewsletterSignup = ($content) => {
    const $newsLetterCheckbox = $content.find('.js-aa-checkbox__input');

    if ($newsLetterCheckbox.length && !$newsLetterCheckbox.prop('checked')) {
      return;
    }

    this.common.sendGoogleEvent(
      'Newsletter - Signup',
      'SignedUp',
      'Private Request',
      {
        ampEventName: 'newsletter_subscribed',
      },
    );
  };
}
